import { createContext, use, useMemo } from "react";
import { type IDocumentType } from "@domain/models/dms";
import type { GQL } from "@binale-tech/shared";

/**
 * Realtime associations data backing the DMS module. All three fields come from
 * the `associations/*` Firebase collections - they tell the DMS "is this doc
 * referenced by anything?" without needing to load the actual records or
 * transaction content.
 */
export type DmsAssociationsData = {
    /** docId → set of accounting record IDs. From
     *  `associations/recordDocuments/{companyId}` (inverted). */
    documentAccountingRecords: Map<string, Set<string>>;
    /** docId → set of bank transaction IDs. From
     *  `associations/bankTransactionDocuments/{companyId}` (inverted). */
    documentBankingTransactions: Map<string, Set<string>>;
    /** docId → set of cash register transaction IDs. From
     *  `associations/cashRegisterTransactionDocuments/{companyId}` (inverted). */
    documentCashRegisterTransactions: Map<string, Set<string>>;
};

// High-frequency cadence - Firebase document stream. Consumers reading only
// document data should go through this context (or the helper hooks below)
// so they don't re-render on records/transactions/types ticks.
interface IDmsDocumentsCtx {
    documents: GQL.IDocument[];
    documentsMap: Map<string, GQL.IDocument>;
}

export const DmsDocumentsContext = createContext<IDmsDocumentsCtx>({
    documents: [],
    documentsMap: new Map(),
});

const EMPTY_DOCUMENTS_MAP: ReadonlyMap<string, GQL.IDocument> = new Map();

export const useDmsDocuments = (): GQL.IDocument[] => use(DmsDocumentsContext).documents;

export const useDmsDocumentsMap = (): ReadonlyMap<string, GQL.IDocument> => use(DmsDocumentsContext).documentsMap;

export const useDmsDocument = (id: string | undefined | null): GQL.IDocument | undefined => {
    const { documentsMap } = use(DmsDocumentsContext);
    return id ? documentsMap.get(id) : undefined;
};

export const useDmsDocumentsByIds = (ids: readonly string[] | undefined | null): ReadonlyMap<string, GQL.IDocument> => {
    const { documentsMap } = use(DmsDocumentsContext);
    return useMemo(() => {
        if (!ids?.length) {
            return EMPTY_DOCUMENTS_MAP;
        }
        const result = new Map<string, GQL.IDocument>();
        for (const id of ids) {
            const doc = documentsMap.get(id);
            if (doc) {
                result.set(id, doc);
            }
        }
        return result;
    }, [documentsMap, ids]);
};

// Rare-cadence - fetched once per visibility change. Splitting these out so
// consumers that need only the type catalogue don't re-render on Firebase
// document streams.
interface IDmsTypesCtx {
    documentTypes: IDocumentType[];
    fetchTypes: () => Promise<void>;
}

export const DmsTypesContext = createContext<IDmsTypesCtx>({
    documentTypes: [],
    fetchTypes: () => Promise.reject(),
});

export const useDmsTypes = (): IDmsTypesCtx => use(DmsTypesContext);

// Medium-cadence - realtime associations collections (records + transactions).
interface IDmsDataCtx {
    dmsAssociationsData: DmsAssociationsData;
}

export const DmsDataContext = createContext<IDmsDataCtx>({
    dmsAssociationsData: {
        documentAccountingRecords: new Map(),
        documentBankingTransactions: new Map(),
        documentCashRegisterTransactions: new Map(),
    },
});
